import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { defer, Observable } from 'rxjs';

import { ApiService } from '../../../../api.service';
import { AuthService } from '../../../../service/auth.service';
import { TalentHappyPathAuthService } from '../../../shared/service/talent-happy-path-auth.service';
import { VIDEO_STEP_TOKEN_HEADER, VideoInterviewStepTokenService } from './video-interview-step-token.service';

/** Mirrors com.aquent.domain.VideoInterviewStatus, whose names are what the wire carries. */
export type VideoInterviewStatus =
  'PENDING' | 'INVITED' | 'IN_PROGRESS' | 'COMPLETED' | 'INGESTED' | 'EXPIRED' | 'DELETED' | 'FAILED';

/**
 * The answer both endpoints give, deliberately vendor-neutral. A null status means the talent never
 * had an interview or the read failed; the two are indistinguishable here and both defer.
 */
export interface VideoInterviewResponse {
  status: VideoInterviewStatus | null;
  interviewId: string | null;
  /** The page to load in the iframe. */
  embedUrl: string | null;
  /** The same page, for the "open in a new tab" escape hatch. */
  externalUrl: string | null;
  completedAt: string | null;
  /**
   * The frequency cap's verdict (BIZ-40530): whether this talent owes any video answers - true when
   * a routed question has not been answered within the window, false when every one was. Null when
   * the service gave no verdict (the deferred answer, a failed read) or predates the cap.
   */
  due?: boolean | null;
}

/** Ingested is Completed plus results we have pulled. */
const ALREADY_RECORDED: VideoInterviewStatus[] = ['COMPLETED', 'INGESTED'];

/**
 * One predicate for both the skip and what the page shows, so the two cannot disagree.
 *
 * The service's `due` decides whenever it is present: a talent who recorded more than the window ago
 * is due again although their status still says Ingested, and one whose latest session failed after
 * an earlier recording inside the window is not due although their status says Failed. Only without a
 * verdict does the status decide, which is what this did before the cap and stays right for the
 * deferred answer.
 */
export function hasAlreadyRecorded(response: VideoInterviewResponse): boolean {
  if (response?.due != null) {
    return !response.due;
  }
  return response?.status != null && ALREADY_RECORDED.includes(response.status);
}

/**
 * Calls talent-app's video interview proxy. Both endpoints answer 200 for every talent-caused
 * outcome, a vendor outage included, so callers branch on `status`, not on HTTP failure.
 *
 * The exception is identity (BIZ-40996). These are the only two funnel calls that will not take a
 * person id on trust: the proxy binds the talent to a credential the server signed and answers an
 * empty 401 or 403 when none vouches for the person being asked about. Both are HTTP failures, so
 * both reach the caller's error handler, where they land on the same deferred experience as an
 * unreachable service.
 *
 * <p>Three credentials can reach it, and the precedence is worth stating exactly, because it is not
 * "all three always travel".
 *
 * <p>The step token is attached below, on its own header, so it is unaffected by the rest. The other
 * two share the `Authorization` header and therefore cannot both travel:
 * `authenticated: true` has `ApiService` add the portal `Token …` credential of a talent who arrived
 * from MyAquent (only when one is actually held, so it costs a funnel-only applicant nothing), and
 * then `TalentHappyPathTokenInterceptorService` replaces `Authorization` outright with the funnel's
 * `Bearer …` whenever a funnel sign-in exists. So the portal credential reaches the server only when
 * there is no funnel sign-in — which is exactly the case it was added for — and the funnel token
 * wins whenever both are held.
 *
 * <p>That is coherent rather than lossy: it matches the server's own precedence, where a positive
 * person id on the funnel token outranks anything else. The one combination it would not serve is a
 * funnel token whose id is still -1 held alongside a portal token and no step token, and that is
 * unreachable — the video step is only entered through quick-apply or a gather, and both of those
 * supply a binding credential of their own.
 */
@Injectable({ providedIn: 'root' })
export class VideoInterviewApiService {

  constructor(private apiService: ApiService,
              private authService: AuthService,
              private funnelAuthService: TalentHappyPathAuthService,
              private stepTokenService: VideoInterviewStepTokenService) {}

  /** Idempotent: a repeat call returns the same interview, so a refresh is safe. */
  provision(personId: number, orderId?: number): Observable<VideoInterviewResponse> {
    let params = new HttpParams().set('personId', String(personId));
    if (orderId != null) {
      params = params.set('orderId', String(orderId));
    }
    return this.apiService.httpWrapper<VideoInterviewResponse>({
      method: 'POST',
      url: '/api/video-interview/provision',
      authenticated: true,
      headers: this.stepTokenHeaders(),
      params
    });
  }

  /** Reads where the talent's interview has got to, without creating one. */
  readStatus(personId: number): Observable<VideoInterviewResponse> {
    const params = new HttpParams().set('personId', String(personId));
    return this.apiService.httpWrapper<VideoInterviewResponse>({
      method: 'GET',
      url: '/api/video-interview/status',
      authenticated: true,
      headers: this.stepTokenHeaders(),
      params
    });
  }

  /**
   * Tells the server the talent used the "open in a new tab" link, so it can be counted in Sumo.
   *
   * Sent with `fetch` and `keepalive` rather than `HttpClient`, because some in-app browsers load
   * the link in place of this page, and unloading the page cancels an ordinary request — losing the
   * clicks this count most needs. `fetch` skips Angular's interceptors, so {@link reportHeaders}
   * adds what they would. It also skips `ApiService`'s sign-out on a 401, which a failed count must
   * never trigger.
   */
  reportOpenedInNewTab(personId: number, orderId?: number, jobPostingId?: number): Observable<void> {
    return this.report('opened-in-new-tab', { personId, orderId, jobPostingId });
  }

  /**
   * Tells the server the recorder hung without posting `interview.finished` and a completed status
   * read moved the talent on instead, so it can be counted in Sumo. Sent the same way as
   * {@link reportOpenedInNewTab}, because the page navigates away straight after.
   */
  reportRecorderStalled(personId: number, orderId?: number, jobPostingId?: number,
                        secondsOnRecorder?: number): Observable<void> {
    return this.report('recorder-stalled', { personId, orderId, jobPostingId, secondsOnRecorder });
  }

  /** A count-only POST to the proxy; absent values are left off the query. */
  private report(path: string, values: Record<string, number | undefined>): Observable<void> {
    const params = new URLSearchParams();
    Object.entries(values)
      .filter(([, value]) => value != null)
      .forEach(([name, value]) => params.set(name, String(value)));
    return defer(async () => {
      const response = await fetch(`/api/video-interview/${path}?${params.toString()}`, {
        method: 'POST',
        headers: this.reportHeaders(),
        keepalive: true
      });
      if (!response.ok) {
        throw new Error(`Report answered HTTP ${response.status}`);
      }
    });
  }

  /**
   * The step token, when this visit was given one. Its own header, never `Authorization`: it is not
   * a login and must not be offered as one. Absent for a logged-in talent, whose JWT already
   * carries a person id the server derived itself.
   */
  private stepTokenHeaders(): HttpHeaders | undefined {
    const stepToken = this.stepTokenService.token;
    return stepToken == null ? undefined : new HttpHeaders().set(VIDEO_STEP_TOKEN_HEADER, stepToken);
  }

  /**
   * What the request would carry through `HttpClient`: the portal login, replaced by the funnel's
   * `Bearer` login when there is one (as `TalentHappyPathTokenInterceptorService` does), the video
   * step token, and the CSRF token Angular copies from its cookie on every POST.
   */
  private reportHeaders(): Record<string, string> {
    const headers: Record<string, string> = { ...(this.authService.getHeaders() ?? {}) };
    const idToken = this.funnelAuthService.authentication?.id_token;
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    const stepToken = this.stepTokenService.token;
    if (stepToken != null) {
      headers[VIDEO_STEP_TOKEN_HEADER] = stepToken;
    }
    const xsrfToken = readCookie('XSRF-TOKEN');
    if (xsrfToken != null) {
      headers['X-XSRF-TOKEN'] = xsrfToken;
    }
    return headers;
  }
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const cookie = document.cookie.split('; ').find(entry => entry.startsWith(prefix));
  return cookie == null ? null : decodeURIComponent(cookie.substring(prefix.length));
}
